import { createSignal, For, Index, Show, type VoidComponent } from "solid-js";
import {
  array,
  forward,
  type InferOutput,
  maxLength,
  object,
  optional,
  partialCheck,
  pipe,
  string,
  trim,
} from "valibot";
import { keyFromSelector } from "i18next";
import pMinDelay from "p-min-delay";
import { isEmpty, times } from "lodash-es";
import { useTranslation } from "../../../../../state/i18n";
import SettingSection from "../../components/SettingSection";
import { SettingTextValue } from "../../components/SettingValue";
import SettingSectionEditButton from "../../components/SettingSectionEditButton";
import SettingSectionEditActions from "../../components/SettingSectionEditActions";
import BillingService, { type InvoiceFieldDto } from "../../../../../services/BillingService";
import { TextAndIconButton } from "../../../../../components/button/Button";
import { createForm, type FormApi } from "../../../../../components/form/createForm";
import { errorTranslation } from "../../../../../utils/validation/base";
import {
  MAX_CHARACTERS_INVOICE_FIELD_LABEL,
  MAX_CHARACTERS_INVOICE_FIELD_VALUE,
  MINIMUM_ACTION_LOADING_DELAY,
} from "../../../../../constants";
import { TextInput } from "../../../../../components/inputs/TextInput";
import { useModalManager } from "../../../../../components/modal/ModalManager";
import GenericFailureModal from "../../../../GenericFailureModal";

const INVOICE_FIELD_COUNT = 2;

const notEmptyMessage = () => errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY));
const maxLengthMessage = () => errorTranslation(keyFromSelector(($) => $.VALIDATION_MAX_LENGTH));

const InvoiceFieldSchema = pipe(
  object({
    label: optional(pipe(string(), trim(), maxLength(MAX_CHARACTERS_INVOICE_FIELD_LABEL, maxLengthMessage()))),
    value: optional(pipe(string(), trim(), maxLength(MAX_CHARACTERS_INVOICE_FIELD_VALUE, maxLengthMessage()))),
  }),
  forward(
    partialCheck([["label"], ["value"]], ({ label, value }) => isEmpty(label) || !isEmpty(value), notEmptyMessage()),
    ["value"],
  ),
  forward(
    partialCheck([["label"], ["value"]], ({ label, value }) => isEmpty(value) || !isEmpty(label), notEmptyMessage()),
    ["label"],
  ),
);

const AdditionalInformationSchema = object({ invoiceFields: array(InvoiceFieldSchema) });

type AdditionalInformationFormData = InferOutput<typeof AdditionalInformationSchema>;
type InvoiceFieldFormData = InferOutput<typeof InvoiceFieldSchema>;

const toFormData = (invoiceFields: InvoiceFieldDto[]): AdditionalInformationFormData => ({
  invoiceFields: times(INVOICE_FIELD_COUNT, (index) => ({
    label: invoiceFields[index]?.label ?? "",
    value: invoiceFields[index]?.value ?? "",
  })),
});

const toInvoiceFields = ({ invoiceFields }: AdditionalInformationFormData): InvoiceFieldDto[] =>
  invoiceFields
    .map(({ label, value }) => ({ label: label ?? "", value: value ?? "" }))
    .filter(({ label }) => !isEmpty(label));

type AdditionalInformationSectionProps = {
  invoiceFields: InvoiceFieldDto[];
};

const AdditionalInformationSection: VoidComponent<AdditionalInformationSectionProps> = (props) => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const patchBillingProfile = BillingService.patchBillingProfile();

  const [editing, setEditing] = createSignal(false);

  const form = createForm<AdditionalInformationFormData>(
    AdditionalInformationSchema,
    async (formData) => {
      await pMinDelay(
        patchBillingProfile.mutateAsync({ invoiceFields: toInvoiceFields(formData) }),
        MINIMUM_ACTION_LOADING_DELAY,
      )
        .then(() => setEditing(false))
        .catch((error: unknown) => open({ content: () => <GenericFailureModal error={error} /> }));
    },
    toFormData(props.invoiceFields),
  );

  const startEditing = () => {
    form.reset(toFormData(props.invoiceFields));
    setEditing(true);
  };

  return (
    <SettingSection
      ariaIdPrefix="additional-info"
      title={t(($) => $.SETTINGS_BILLING_ADDITIONAL_INFORMATION_TITLE)}
      description={t(($) => $.SETTINGS_BILLING_ADDITIONAL_INFORMATION_DESCRIPTION)}
    >
      <Show
        when={editing()}
        fallback={<AdditionalInformationDisplay invoiceFields={props.invoiceFields} startEditing={startEditing} />}
      >
        <AdditionalInformationEdit form={form} stopEditing={() => setEditing(false)} />
      </Show>
    </SettingSection>
  );
};

type AdditionalInformationDisplayProps = {
  invoiceFields: InvoiceFieldDto[];
  startEditing: () => void;
};

const AdditionalInformationDisplay: VoidComponent<AdditionalInformationDisplayProps> = (props) => {
  const { t } = useTranslation();
  return (
    <Show
      when={!isEmpty(props.invoiceFields)}
      fallback={
        <TextAndIconButton
          class="ml-auto"
          variant="light"
          icon="plus"
          text={t(($) => $.SETTINGS_BILLING_ADDITIONAL_INFORMATION_ADD_LABEL)}
          onClick={props.startEditing}
        />
      }
    >
      <For each={props.invoiceFields}>
        {(invoiceField) => (
          <div class="min-w-0 flex-1">
            <SettingTextValue label={invoiceField.label}>{invoiceField.value}</SettingTextValue>
          </div>
        )}
      </For>
      <SettingSectionEditButton
        editLabel={t(($) => $.SETTINGS_BILLING_ADDITIONAL_INFORMATION_EDIT_LABEL)}
        editing={false}
        setEditing={props.startEditing}
      />
    </Show>
  );
};

type InvoiceFieldInputProps = {
  value?: string;
  errorMessage?: string;
  ariaLabel: string;
  placeholder: string;
  onBlur: (value: string) => void;
};

const InvoiceFieldInput: VoidComponent<InvoiceFieldInputProps> = (props) => (
  <div class="min-w-0 flex-1">
    <TextInput
      label=""
      ariaLabel={props.ariaLabel}
      placeholder={props.placeholder}
      value={props.value}
      errorMessage={props.errorMessage}
      onBlur={(e) => props.onBlur(e.currentTarget.value)}
    />
  </div>
);

type AdditionalInformationEditProps = {
  form: FormApi<AdditionalInformationFormData>;
  stopEditing: () => void;
};

const AdditionalInformationEdit: VoidComponent<AdditionalInformationEditProps> = (props) => {
  const { t } = useTranslation();

  const updateInvoiceField = (index: number, key: keyof InvoiceFieldFormData, value: string) =>
    props.form.updateValue(
      "invoiceFields",
      props.form.getValue("invoiceFields")?.map((field, i) => (i === index ? { ...field, [key]: value } : field)),
    );

  return (
    <div class="flex grow flex-col gap-1">
      <span class="text-base-semibold text-secondary">
        {t(($) => $.SETTINGS_BILLING_ADDITIONAL_INFORMATION_EDITING_LABEL)}
      </span>
      <div class="flex min-w-0 grow flex-col gap-3">
        <Index each={props.form.getValue("invoiceFields")}>
          {(invoiceField, index) => (
            <div class="flex gap-3">
              <InvoiceFieldInput
                value={invoiceField().label}
                errorMessage={props.form.getError(`invoiceFields.${index}.label`)}
                onBlur={(value) => updateInvoiceField(index, "label", value)}
                ariaLabel={t(($) => $.SETTINGS_BILLING_ADDITIONAL_INFORMATION_KEY_INPUT_ARIA_LABEL, {
                  number: index + 1,
                })}
                placeholder={t(($) => $.SETTINGS_BILLING_ADDITIONAL_INFORMATION_KEY_INPUT_PLACEHOLDER)}
              />
              <InvoiceFieldInput
                value={invoiceField().value}
                errorMessage={props.form.getError(`invoiceFields.${index}.value`)}
                onBlur={(value) => updateInvoiceField(index, "value", value)}
                ariaLabel={t(($) => $.SETTINGS_BILLING_ADDITIONAL_INFORMATION_VALUE_INPUT_ARIA_LABEL, {
                  number: index + 1,
                })}
                placeholder={t(($) => $.SETTINGS_BILLING_ADDITIONAL_INFORMATION_VALUE_INPUT_PLACEHOLDER)}
              />
            </div>
          )}
        </Index>
        <div class="mt-3 ml-auto">
          <SettingSectionEditActions
            form={props.form}
            editing
            stopEditing={props.stopEditing}
            cancelLabel={t(($) => $.SETTINGS_BILLING_ADDITIONAL_INFORMATION_CANCEL_LABEL)}
            saveLabel={t(($) => $.SETTINGS_BILLING_ADDITIONAL_INFORMATION_SAVE_LABEL)}
            savingLabel={t(($) => $.SETTINGS_BILLING_ADDITIONAL_INFORMATION_SAVING_LABEL)}
          />
        </div>
      </div>
    </div>
  );
};

export default AdditionalInformationSection;
